// The in-place deal modals for a booking board (ART-139).
//
// Clicking a card opens its record OVER the current page rather than navigating: an OFFER reopens the offer form
// as EDIT OFFER (ART-326 — from there, Send to team), a HOLD opens the show editor, a CONFIRMED show opens the
// read-only preview. Closing returns to the board the user was on — no "opened a modal, closed it, and I'm on a
// different page" (PO). This is the same transient-band-context trick the "+ New offer" flow uses: the scope
// shell has no BandProvider, so the modal is mounted under a `RosterBandProvider` for the card's own band,
// which also gives the offer form its band-scoped mutations. On the per-artist board the band is the one
// already mounted, so the provider is a cache hit.
//
// The show editor and preview navigate nowhere internally — their sub-modals (guests, share) are route-level,
// never editor buttons — so mounting them here is self-contained. The preview's two DEEP secondary actions
// (guest list / door) are the exception: they have no in-place form, so they fall back to `onNavigate`.
import { useState } from "react";
import { ActionModal, Modal, Spinner } from "@cntrl/ui";
import { useConfirmHold, useDeleteShow } from "../../app/hooks/useShows.js";
import { pushToast } from "../../app/toasts.js";
import { RosterBandProvider } from "../../shells/roster/RosterBandProvider.js";
import { ShowEditorModal } from "../booking/ShowEditorModal.js";
import { ShowPreview } from "../booking/ShowPreview.js";
import { NewOfferModal } from "./NewOfferModal.js";
import { ShowRequestDetails } from "../booking/ShowRequestDetails.js";
import { useBookingOffers, useDeleteBookingOffer } from "../../app/hooks/useBookingOffers.js";
import { SendToTeamModal } from "./SendToTeamModal.js";
import styles from "./RosterDealModal.module.scss";

/** Which card was clicked. All three carry the card's OWN band, since the scope board spans many. */
export type RosterDealTarget =
  | { kind: "offer"; bandId: string; offerId: string }
  | { kind: "hold"; bandId: string; showId: string }
  | { kind: "confirmed"; bandId: string; showId: string }
  // Needs attention's Open (ART-324): the FULL editor whatever the show's status — a confirmed show opens to
  // edit there, not to the read-only preview a board card gives it.
  | { kind: "show"; bandId: string; showId: string }
  // A show request's Details (ART-327) — what Mgmt / the artist AV open and answer.
  | { kind: "request"; bandId: string; offerId: string }
  // The cross-roster board's Ready-to-confirm verbs (ART-330): finish the deal, or kill it.
  | { kind: "confirm" | "cancel"; bandId: string; showId: string; label: string }
  // The booking calendar's soft-hold verbs (ART-331): send one to the team, or release one or a night's worth.
  | { kind: "send"; bandId: string; offerId: string }
  | { kind: "release"; bandId: string; offerIds: string[]; label: string };

export interface RosterDealModalProps {
  target: RosterDealTarget | null;
  /** Close the modal and return to the board — the caller also refreshes it here. */
  onClose: () => void;
  /** The confirmed preview's deep guest/door actions, which have no in-place form (bare path). */
  onNavigate?: (route: string) => void;
}

export function RosterDealModal({ target, onClose, onNavigate }: RosterDealModalProps) {
  if (!target) return null;
  return (
    <RosterBandProvider artistId={target.bandId}>
      <DealModalInner target={target} onClose={onClose} onNavigate={onNavigate} />
    </RosterBandProvider>
  );
}

const noop = () => {};

/** What is on screen right now. It can TRANSITION: an offer's "Convert to hold" swaps to the editor in
 *  create mode; a preview's "Edit" swaps to the editor — both without leaving the page. */
type View =
  | { t: "offer" }
  | { t: "editor"; showId: string }
  | { t: "preview"; showId: string };

function DealModalInner({ target, onClose, onNavigate }: {
  target: RosterDealTarget;
  onClose: () => void;
  onNavigate?: (route: string) => void;
}) {
  if (target.kind === "request") return <ShowRequestDetails offerId={target.offerId} onClose={onClose} />;
  if (target.kind === "confirm" || target.kind === "cancel") return <ReadyDealAction target={target} onClose={onClose} />;
  if (target.kind === "send") return <SendOffer offerId={target.offerId} onClose={onClose} />;
  if (target.kind === "release") return <ReleaseOffers target={target} onClose={onClose} />;
  return <DealView target={target} onClose={onClose} onNavigate={onNavigate} />;
}

function DealView({ target, onClose, onNavigate }: {
  target: RosterDealTarget;
  onClose: () => void;
  onNavigate?: (route: string) => void;
}) {
  // The offer's FULL record (with the notes the kanban row does not carry), read band-scoped. Cache-shared
  // with the per-artist board's own shortlist read.
  const offersQuery = useBookingOffers({ enabled: target.kind === "offer" });
  const offerId = target.kind === "offer" ? target.offerId : null;
  const [step, setStep] = useState(1);
  const [view, setView] = useState<View>(() =>
    target.kind === "hold" || target.kind === "show"
      ? { t: "editor", showId: target.showId }
      : target.kind === "confirmed"
        ? { t: "preview", showId: target.showId }
        : { t: "offer" },
  );

  if (view.t === "offer") {
    if (offersQuery.isLoading) {
      return (
        <Modal title="Offer" size="sm" hideHeader onClose={onClose}>
          <div className={styles.loading}>
            <Spinner size={22} label="Loading offer…" />
          </div>
        </Modal>
      );
    }
    const offer = offersQuery.data?.offers?.find((o) => o.offerId === offerId);
    if (!offer) {
      // Gone since the board was drawn (passed or converted in another tab). The board refetches on close.
      return (
        <Modal title="Offer" size="sm" onClose={onClose}>
          <p className={styles.gone}>This offer is no longer on the shortlist.</p>
        </Modal>
      );
    }
    // ART-326: an offer card reopens the offer form as EDIT OFFER (and from there, Send to team) — the same
    // form the per-artist board opens. A hold is born only from the team's answer, so there is no Convert here.
    return <NewOfferModal offer={offer} onClose={onClose} />;
  }

  if (view.t === "editor") {
    return (
      <ShowEditorModal
        showId={view.showId}
        step={step}
        onStep={setStep}
        onCreated={noop}
        onShowCreated={noop}
        onExit={onClose}
        onDeleted={onClose}
        onSaved={onClose}
      />
    );
  }

  const guests = (id: string) => `/roster/artists/${target.bandId}/booking/shows/${id}/guests`;
  return (
    <ShowPreview
      showId={view.showId}
      onBack={onClose}
      // Edit swaps to the editor IN PLACE — the same modal, no navigation.
      onEdit={(id) => {
        setStep(1);
        setView({ t: "editor", showId: id });
      }}
      // The guest list is a deep crew flow with no in-place form; it falls back to the routed destination.
      onOpenGuests={(id) => onNavigate?.(guests(id))}
    />
  );
}

/**
 * Confirm show / Cancel show on a Ready-to-confirm card of the CROSS-ROSTER board (ART-330). That board has no band
 * mounted, so the verbs run here, under the deal's own band (the provider `RosterDealModal` mounts) — the same calls
 * the per-artist board makes: a confirm puts the show out to everyone as confirmed; a cancel takes it off every
 * surface and tells everyone attached.
 */
function ReadyDealAction({ target, onClose }: {
  target: Extract<RosterDealTarget, { kind: "confirm" | "cancel" }>;
  onClose: () => void;
}) {
  const confirmHold = useConfirmHold();
  const deleteShow = useDeleteShow();
  const confirming = target.kind === "confirm";
  return (
    <ActionModal
      title={confirming ? "Confirm show?" : "Cancel show?"}
      onCancel={onClose}
      cancelLabel={confirming ? "Not yet" : "Keep show"}
      confirmLabel={confirming ? "Confirm show" : "Cancel show"}
      confirmTone={confirming ? undefined : "danger"}
      loading={confirmHold.isPending || deleteShow.isPending}
      onConfirm={() =>
        confirming
          ? confirmHold.mutate(target.showId, {
              onSuccess: () => {
                pushToast(`${target.label} confirmed. Everyone on it was told.`, "success");
                onClose();
              },
            })
          : deleteShow.mutate(target.showId, {
              onSuccess: () => {
                pushToast(`${target.label} canceled. Everyone attached was notified.`, "info");
                onClose();
              },
            })
      }
    >
      {confirming
        ? `Confirm ${target.label}? It goes out to everyone as a confirmed show.`
        : `Cancel ${target.label}? Everyone on this show will be told.`}
    </ActionModal>
  );
}

/** Send to team from the booking calendar (ART-331): the board's own editor, on the offer read under its band. */
function SendOffer({ offerId, onClose }: { offerId: string; onClose: () => void }) {
  const offers = useBookingOffers();
  if (offers.isLoading) {
    return (
      <Modal title="Send to team" size="sm" hideHeader onClose={onClose}>
        <div className={styles.loading}><Spinner size={22} label="Loading offer…" /></div>
      </Modal>
    );
  }
  const offer = offers.data?.offers?.find((o) => o.offerId === offerId);
  if (!offer) {
    return (
      <Modal title="Send to team" size="sm" onClose={onClose}>
        <p className={styles.gone}>This offer is no longer on the shortlist.</p>
      </Modal>
    );
  }
  return <SendToTeamModal offer={offer} onClose={onClose} />;
}

/** Release one soft hold, or every soft hold on a night (ART-331) — the board's Delete, asked first. */
function ReleaseOffers({ target, onClose }: {
  target: Extract<RosterDealTarget, { kind: "release" }>;
  onClose: () => void;
}) {
  const del = useDeleteBookingOffer();
  const n = target.offerIds.length;
  return (
    <ActionModal
      title={n > 1 ? `Release all ${n}?` : "Release this soft hold?"}
      onCancel={onClose}
      cancelLabel="Keep"
      confirmLabel={n > 1 ? "Release all" : "Release"}
      confirmTone="danger"
      loading={del.isPending}
      onConfirm={() =>
        void Promise.all(target.offerIds.map((id) => del.mutateAsync(id))).then(() => {
          pushToast(n > 1 ? `Released ${n} soft holds.` : `${target.label} released.`, "success");
          onClose();
        })
      }
    >
      {n > 1
        ? `Release every soft hold on ${target.label}? Only your agency saw them.`
        : `Release ${target.label}? Only your agency saw it.`}
    </ActionModal>
  );
}
